import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { employeeService, leaveService } from '../services/hrService';
import { formatNumber } from '../utils/format';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    todayAttendance: 0,
    pendingLeaves: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [employees, leaves] = await Promise.all([
        employeeService.getAll(),
        leaveService.getRequests({ status: 'Pending' }),
      ]);

      const employeeList = employees.data.results || employees.data;
      const activeEmployees = (Array.isArray(employeeList) ? employeeList : []).filter(e => e.is_active);
      const leaveList = leaves.data.results || leaves.data;

      setStats({
        totalEmployees: employees.data.count || (Array.isArray(employeeList) ? employeeList.length : 0),
        activeEmployees: activeEmployees.length,
        todayAttendance: 0, // Will be calculated from attendance data
        pendingLeaves: leaves.data.count || (Array.isArray(leaveList) ? leaveList.length : 0),
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Total Employees',
      value: formatNumber(stats.totalEmployees),
      icon: UsersIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Employees',
      value: formatNumber(stats.activeEmployees),
      icon: UsersIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Today\'s Attendance',
      value: formatNumber(stats.todayAttendance),
      icon: ClockIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Pending Leave Requests',
      value: formatNumber(stats.pendingLeaves),
      icon: CalendarIcon,
      color: 'bg-purple-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-700">
          Overview of your HR management system
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.name}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`${card.color} rounded-md p-3`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {card.name}
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {card.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Quick Actions
            </h3>
            <div className="mt-5 grid grid-cols-1 gap-3">
              <a
                href="/employees"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                Add New Employee
              </a>
              <a
                href="/attendance"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                Record Attendance
              </a>
              <a
                href="/leaves"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                View Leave Requests
              </a>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Activity
            </h3>
            <div className="mt-5">
              <p className="text-sm text-gray-500">No recent activity</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
